import { Delay, Execute, IParamHandle, SequenceChain, consumer, inject, param } from "@spawn/core";
import { GoldenBonusService } from "@spawn/golden-bonus";
import { PaylineType } from "@spawn/networking";
import {
	hideAllSymbolsOnSpotlight,
	hideLineMarkerEvent,
	hideLinePingerEvent,
	hideSymbolsOnSpotlight,
	showLineMarkerEvent,
	showLinePingerEvent,
	showSymbolsOnSpotlight,
} from "@spawn/slot-machine";
import { createShuffledRandomSelector } from "../utils/shuffledRandomSelector";
import * as SlotMachine from "@spawn/slot-machine";
import { BaseSymbolTypes } from "../services/symbolService";

@consumer
export class PingerModule extends SlotMachine.PingerModule {
	@inject protected readonly goldenBonusService!: GoldenBonusService;

	@param({ default: 1.5 }) public readonly symbolAnimationDuration!: IParamHandle<number>;
	@param({ default: 1.5 }) public readonly coinAnimationDuration!: IParamHandle<number>;

	private randomSelector?: Generator<number, never, void>;

	protected filterDoubleSymbols(symbolCoordinates: { x: number; y: number }[]): { x: number; y: number }[] {
		return symbolCoordinates.filter((v, i, a) => a.findIndex((v2) => v2.x === v.x && v2.y === v.y) === i);
	}

	public override async start(): Promise<void> {
		const totalSounds = this.winSoundService.linePingSounds().length;
		this.randomSelector = createShuffledRandomSelector(totalSounds);

		const winningPaylines = this.partyService.paylineWins.filter(
			({ winAmount }) => this.ignoreZeroWins() && winAmount && winAmount > 0
		);

		const paylineSymbols = this.filterDoubleSymbols(winningPaylines.flatMap((payline) => payline.symbolCoordinates));
		const paylineSymbolPhase =
			paylineSymbols.length > 0
				? new SequenceChain(
						new Execute(() => {
							this.eventDispatcher.dispatch(hideAllSymbolsOnSpotlight());
							this.eventDispatcher.dispatch(
								showSymbolsOnSpotlight({
									symbolCoordinates: paylineSymbols,
									symbolMetaDataKeys: [SlotMachine.SymbolMetaDataKeys.WinAnimation],
									loopAnimations: true,
								})
							);
						}),
						new Delay(this.symbolAnimationDuration()),
						new Execute(() => {
							this.eventDispatcher.dispatch(hideSymbolsOnSpotlight(paylineSymbols));
							this.onSymbolsHidden(paylineSymbols);
						})
					)
				: null;

		const bonusSymbols = this.goldenBonusService
			.getBonusSymbols()
			.filter((symbol) => symbol.symbol !== BaseSymbolTypes.ScatterFG);
		const coinCoordinates = bonusSymbols.map((symbol) => ({ x: symbol.position.x, y: symbol.position.y }));
		const coinPhase =
			coinCoordinates.length > 0
				? new SequenceChain(
						new Execute(() => {
							this.eventDispatcher.dispatch(
								showSymbolsOnSpotlight({
									symbolCoordinates: coinCoordinates,
									symbolMetaDataKeys: [SlotMachine.SymbolMetaDataKeys.WinAnimation],
									loopAnimations: true,
								})
							);
							this.onCoinPhaseStart();
						}),
						new Delay(this.coinAnimationDuration()),
						new Execute(() => {
							this.eventDispatcher.dispatch(hideSymbolsOnSpotlight(coinCoordinates));
							this.onSymbolsHidden(coinCoordinates);
						})
					)
				: null;

		const lineAnimationSequence = winningPaylines.map(({ paylineId, type, symbolCoordinates }, index) => {
			const duration = this.calculateStepDuration(index + 1, winningPaylines.length);
			return new SequenceChain(
				new Execute(() => {
					this.eventDispatcher.dispatch(
						showSymbolsOnSpotlight({ symbolCoordinates, symbolMetaDataKeys: this.visibleMetaDataKeys() })
					);

					if (type !== PaylineType.Normal) {
						return;
					}

					this.eventDispatcher.dispatch(showLinePingerEvent(paylineId));
					this.eventDispatcher.dispatch(showLineMarkerEvent(paylineId));

					this.activePaylineId = paylineId;
				}),
				new Execute(() => {
					const soundIndex = this.randomSelector!.next().value;
					const pingSound = this.winSoundService.getLinePingSound(soundIndex);

					void pingSound.play();
				}),
				new Delay(duration / 1000),
				new Execute(() => {
					this.eventDispatcher.dispatch(hideSymbolsOnSpotlight(symbolCoordinates));
					this.onSymbolsHidden(symbolCoordinates);

					if (type !== PaylineType.Normal) {
						return;
					}
					this.eventDispatcher.dispatch(hideLinePingerEvent(paylineId));
					this.eventDispatcher.dispatch(hideLineMarkerEvent(paylineId));
					this.activePaylineId = undefined;
				})
			);
		});

		this.chain = new SequenceChain();
		// The payline symbols play together only as the lead-in to the coin phase.
		if (paylineSymbolPhase && coinPhase) {
			this.chain.addSegments(paylineSymbolPhase);
		}
		if (coinPhase) {
			this.chain.addSegments(coinPhase);
		}
		if (!this.skipSingleLineWins || winningPaylines.length > 1) {
			this.chain.addSegments(...lineAnimationSequence);
		}

		await this.chain.play();
	}

	/** Called when symbols leave the spotlight. */
	protected onSymbolsHidden(_symbolCoordinates: { x: number; y: number }[]): void {}

	/** Called when the coins start to play on the spotlight. */
	protected onCoinPhaseStart(): void {}
}
